import { Sparkles } from "lucide-react";
import { useLayoutEffect, useRef } from "react";

import { ChatSaveResourceAction } from "~/ai-chat/feature/chat-save-resource-action";
import { ChatVerifiedEntityChip } from "~/ai-chat/feature/chat-verified-entity-chip";
import { isNearBottom } from "~/ai-chat/feature/stick-to-bottom";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import { ChatErrorBubble } from "~/ai-chat/ui/chat-error-bubble";
import type { RenderEntityChip, RenderSaveResourceAction } from "~/ai-chat/ui/chat-markdown";
import { ChatMessageBubble, ChatMessageTypingBubble } from "~/ai-chat/ui/chat-message-bubble";
import { ChatSuggestionChip } from "~/ai-chat/ui/chat-suggestion-chip";
import { useCurrentUser } from "~/user/feature/use-current-user";

// Module-level so the markdown render context keeps one identity across stream deltas.
const renderSaveResourceAction: RenderSaveResourceAction = (action, blockKey) => (
  <ChatSaveResourceAction key={blockKey} action={action} />
);
const renderEntityChip: RenderEntityChip = (props) => <ChatVerifiedEntityChip {...props} />;

const SUGGESTIONS = [
  "Help with the system I need to create",
  "Help finding a resource",
  "Create Readme AI for a system",
  "Create a new branch",
];

interface AiChatTranscriptProps {
  isPending: boolean;
  activity?: string | null;
  onSuggestionSelect: (suggestion: string) => void;
  // Re-send a failed user turn (offered on error bubbles).
  onRetry: (content?: string) => void;
  // Reword a user turn: truncate the transcript there and re-ask with the new text.
  onEdit: (messageId: string, content: string) => void;
  // When set, file previews open in the page's canvas pane instead of inline.
  onOpenArtifact?: (file: FilePreviewRequest) => void;
}

export function AiChatTranscript({
  isPending,
  activity,
  onSuggestionSelect,
  onRetry,
  onEdit,
  onOpenArtifact,
}: AiChatTranscriptProps) {
  const { messages } = useAiChat();
  const currentUser = useCurrentUser();
  const userName = currentUser.status === "ready" ? currentUser.name : undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  const handleScroll = () => {
    const el = containerRef.current;
    if (el === null) return;
    stickToBottom.current = isNearBottom(el);
  };

  // Follow the stream to the bottom on every token (the messages array is replaced per delta),
  // but only when the user is parked at the bottom — so scrolling up to read history isn't yanked
  // back down. Layout effect + instant jump avoids flicker.
  useLayoutEffect(() => {
    if (!stickToBottom.current) return;
    const el = containerRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [messages, isPending, activity]);

  if (messages.length === 0 && !isPending) {
    return <AiChatEmptyState userName={userName} onSuggestionSelect={onSuggestionSelect} />;
  }

  const lastItemId = messages.at(-1)?.id;

  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 py-4">
      {messages.map((item) => {
        if (item.kind === "error") {
          return (
            <ChatErrorBubble
              key={item.id}
              error={item}
              onRetry={isPending ? undefined : () => onRetry(item.userContent)}
            />
          );
        }
        // Edit and resend are offered on user turns while idle (both truncate + re-ask).
        const canEdit = item.role === "user" && !isPending;
        return (
          <ChatMessageBubble
            key={item.id}
            message={item}
            userName={userName}
            isStreaming={isPending && item.id === lastItemId}
            onEdit={canEdit ? (content) => onEdit(item.id, content) : undefined}
            onRetry={canEdit ? () => onEdit(item.id, item.content) : undefined}
            onOpenArtifact={onOpenArtifact}
            renderSaveResourceAction={renderSaveResourceAction}
            renderEntityChip={renderEntityChip}
          />
        );
      })}
      {isPending && <ChatMessageTypingBubble label={activity ?? undefined} />}
    </div>
  );
}

function firstName(name: string | undefined): string | undefined {
  return name?.trim().split(/\s+/)[0] || undefined;
}

function AiChatEmptyState({
  userName,
  onSuggestionSelect,
}: {
  userName: string | undefined;
  onSuggestionSelect: (suggestion: string) => void;
}) {
  const greeting = firstName(userName) ? `Hello ${firstName(userName)}!` : "Hello!";
  return (
    <div className="flex flex-1 flex-col justify-center overflow-y-auto px-6 py-4">
      <div className="flex flex-col items-start gap-3">
        <div className="bg-muted border-background flex size-16 items-center justify-center rounded-full border-2">
          <Sparkles className="text-primary size-7" />
        </div>
        <div className="flex flex-col items-start gap-3">
          <div className="space-y-0.5">
            <p className="text-secondary-foreground text-lg">{greeting}</p>
            <p className="text-secondary-foreground text-sm">How would you like to start?</p>
          </div>
          {SUGGESTIONS.map((suggestion) => (
            <ChatSuggestionChip key={suggestion} label={suggestion} onClick={() => onSuggestionSelect(suggestion)} />
          ))}
        </div>
      </div>
    </div>
  );
}
